'use client';

import React, { useEffect, useRef, useState } from 'react';
import styles from './loop-video.module.scss';

export type LoopVideoProps = {
  /** Path without extension, e.g. `/assets/landing/basics` → .webm, .mp4, -poster.webp. */
  src: string;
  /** CSS aspect ratio of the frame, default 3 / 2. */
  ratio?: string;
  className?: string;
};

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const query = window.matchMedia(REDUCED_MOTION);
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  return reduced;
}

/**
 * Decorative, muted looping clip. Loads nothing until it scrolls into view,
 * plays only while visible and falls back to the poster for reduced motion.
 */
export default function LoopVideo({ src, ratio = '3 / 2', className }: LoopVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const reducedMotion = usePrefersReducedMotion();
  const poster = `${src}-poster.webp`;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || reducedMotion) return undefined;
    // React does not reliably reflect `muted` as an attribute; autoplay policies need it.
    video.muted = true;
    const play = () => {
      const result = video.play?.();
      if (result && typeof result.catch === 'function') result.catch(() => undefined);
    };
    if (typeof IntersectionObserver === 'undefined') {
      play();
      return () => video.pause?.();
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) play();
        else video.pause?.();
      },
      { threshold: 0.15 }
    );
    observer.observe(video);
    return () => {
      observer.disconnect();
      video.pause?.();
    };
  }, [reducedMotion]);

  return (
    <div
      className={`${styles.frame} ${className || ''}`}
      style={{ aspectRatio: ratio }}
      aria-hidden="true"
      data-testid="loop-video"
    >
      {reducedMotion ? (
        <img src={poster} alt="" loading="lazy" decoding="async" data-testid="loop-video-poster" />
      ) : (
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          preload="none"
          poster={poster}
          tabIndex={-1}
          disablePictureInPicture
          data-testid="loop-video-player"
        >
          <source src={`${src}.webm`} type="video/webm" />
          <source src={`${src}.mp4`} type="video/mp4" />
        </video>
      )}
    </div>
  );
}
